/**
 * PinPad CDP Error Codes Catalog
 * 
 * Centralized error code definitions based on CDP PinPadWeb RETAIL v2.6.0 manual.
 * Includes error descriptions, user-friendly messages, and retry recommendations.
 * 
 * @module config/pinpad-v2/error-codes
 */

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  INFO: 'INFO',           // Informational, no action needed
  WARNING: 'WARNING',     // Warning, operation may continue
  ERROR: 'ERROR',         // Error, operation failed but retryable
  CRITICAL: 'CRITICAL'    // Critical error, requires immediate attention
};

/**
 * Error categories
 */
export const ErrorCategory = {
  CONNECTION: 'CONNECTION',       // Connection/communication errors
  AUTHENTICATION: 'AUTHENTICATION', // Login/authentication errors
  CARD: 'CARD',                   // Card reading errors
  TRANSACTION: 'TRANSACTION',     // Transaction processing errors
  TERMINAL: 'TERMINAL',           // Terminal hardware/state errors
  VALIDATION: 'VALIDATION',       // Data validation errors
  TIMEOUT: 'TIMEOUT',             // Timeout errors
  NETWORK: 'NETWORK',             // Network communication errors
  UNKNOWN: 'UNKNOWN'              // Unknown errors
};

/**
 * CDP Error Codes Catalog
 * Based on CDP PinPadWeb RETAIL v2.6.0 documentation
 */
export const CDP_ERROR_CODES = {
  // Connection & Communication Errors (00xx)
  '0001': {
    code: '0001',
    message: 'Error de conexión con el PinPad',
    userMessage: 'No se pudo conectar con el dispositivo de pago',
    category: ErrorCategory.CONNECTION,
    severity: ErrorSeverity.ERROR,
    retryable: true,
    retryDelay: 2000,
    maxRetries: 3
  },
  '0002': {
    code: '0002',
    message: 'Timeout en comunicación con PinPad',
    userMessage: 'El dispositivo de pago no responde',
    category: ErrorCategory.TIMEOUT,
    severity: ErrorSeverity.ERROR,
    retryable: true,
    retryDelay: 3000,
    maxRetries: 2
  },
  '0003': {
    code: '0003',
    message: 'PinPad no disponible',
    userMessage: 'El dispositivo de pago no está disponible',
    category: ErrorCategory.CONNECTION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0004': {
    code: '0004',
    message: 'Chrome Extension no encontrada',
    userMessage: 'Extensión de pago no instalada. Contacte al administrador',
    category: ErrorCategory.CONNECTION,
    severity: ErrorSeverity.CRITICAL,
    retryable: false
  },

  // Authentication Errors (01xx)
  '0100': {
    code: '0100',
    message: 'Error de autenticación',
    userMessage: 'Error al iniciar sesión en el dispositivo de pago',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.ERROR,
    retryable: true,
    maxRetries: 2
  },
  '0101': {
    code: '0101',
    message: 'Credenciales inválidas',
    userMessage: 'Credenciales de acceso incorrectas',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0102': {
    code: '0102',
    message: 'Usuario no autorizado',
    userMessage: 'Usuario sin permisos para realizar pagos',
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },

  // Card Reading Errors (02xx)
  '0200': {
    code: '0200',
    message: 'Error al leer tarjeta',
    userMessage: 'No se pudo leer la tarjeta. Intente nuevamente',
    category: ErrorCategory.CARD,
    severity: ErrorSeverity.WARNING,
    retryable: true,
    maxRetries: 3
  },
  '0201': {
    code: '0201',
    message: 'Tarjeta no insertada',
    userMessage: 'Por favor inserte la tarjeta',
    category: ErrorCategory.CARD,
    severity: ErrorSeverity.INFO,
    retryable: true
  },
  '0202': {
    code: '0202',
    message: 'Tarjeta retirada antes de tiempo',
    userMessage: 'No retire la tarjeta hasta que se indique',
    category: ErrorCategory.CARD,
    severity: ErrorSeverity.WARNING,
    retryable: true
  },
  '0203': {
    code: '0203',
    message: 'Chip de tarjeta ilegible',
    userMessage: 'Chip dañado. Intente con otra tarjeta',
    category: ErrorCategory.CARD,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0204': {
    code: '0204',
    message: 'Banda magnética ilegible',
    userMessage: 'No se pudo leer la banda magnética',
    category: ErrorCategory.CARD,
    severity: ErrorSeverity.WARNING,
    retryable: true,
    maxRetries: 2
  },

  // Transaction Errors (03xx)
  '0300': {
    code: '0300',
    message: 'Transacción declinada',
    userMessage: 'La transacción fue rechazada',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0301': {
    code: '0301',
    message: 'Fondos insuficientes',
    userMessage: 'Fondos insuficientes en la tarjeta',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0302': {
    code: '0302',
    message: 'Tarjeta expirada',
    userMessage: 'La tarjeta ha expirado',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0303': {
    code: '0303',
    message: 'PIN incorrecto',
    userMessage: 'PIN incorrecto. Intente nuevamente',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.WARNING,
    retryable: true,
    maxRetries: 2
  },
  '0304': {
    code: '0304',
    message: 'PIN bloqueado',
    userMessage: 'Tarjeta bloqueada por intentos de PIN',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0305': {
    code: '0305',
    message: 'Transacción cancelada por usuario',
    userMessage: 'Transacción cancelada',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.INFO,
    retryable: false
  },
  '0306': {
    code: '0306',
    message: 'Monto inválido',
    userMessage: 'El monto de la transacción es inválido',
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.ERROR,
    retryable: false
  },
  '0307': {
    code: '0307',
    message: 'Transacción duplicada',
    userMessage: 'Esta transacción ya fue procesada',
    category: ErrorCategory.TRANSACTION,
    severity: ErrorSeverity.WARNING,
    retryable: false
  },

  // Terminal Errors (04xx)
  '0400': {
    code: '0400',
    message: 'Terminal no inicializado',
    userMessage: 'El dispositivo no está listo. Reiniciando...',
    category: ErrorCategory.TERMINAL,
    severity: ErrorSeverity.ERROR,
    retryable: true,
    maxRetries: 2
  },
  '0401': {
    code: '0401',
    message: 'Terminal ocupado',
    userMessage: 'El dispositivo está procesando otra operación',
    category: ErrorCategory.TERMINAL,
    severity: ErrorSeverity.WARNING,
    retryable: true,
    retryDelay: 2000
  },
  '0402': {
    code: '0402',
    message: 'Error de hardware',
    userMessage: 'Error en el dispositivo. Contacte soporte',
    category: ErrorCategory.TERMINAL,
    severity: ErrorSeverity.CRITICAL,
    retryable: false
  },

  // Network Errors (05xx)
  '0500': {
    code: '0500',
    message: 'Error de red',
    userMessage: 'Error de conexión a la red',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.ERROR,
    retryable: true,
    retryDelay: 3000,
    maxRetries: 3
  },
  '0501': {
    code: '0501',
    message: 'Timeout de red',
    userMessage: 'La red no responde',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.ERROR,
    retryable: true,
    retryDelay: 5000,
    maxRetries: 2
  },
  '0502': {
    code: '0502',
    message: 'Servidor no disponible',
    userMessage: 'Servicio de pagos no disponible',
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.CRITICAL,
    retryable: true,
    retryDelay: 5000,
    maxRetries: 2
  },

  // Unknown/Generic Errors (99xx)
  '9999': {
    code: '9999',
    message: 'Error desconocido',
    userMessage: 'Ocurrió un error inesperado',
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.ERROR,
    retryable: false
  }
};

/**
 * Get error details by code
 * @param {string} code - Error code
 * @returns {Object|null} Error details or null if not found
 */
export function getErrorByCode(code) {
  return CDP_ERROR_CODES[code] || null;
}

/**
 * Get user-friendly error message
 * @param {string} code - Error code
 * @returns {string} User-friendly message
 */
export function getUserMessage(code) {
  const error = getErrorByCode(code);
  return error ? error.userMessage : 'Ocurrió un error inesperado';
}

/**
 * Check if error is retryable
 * @param {string} code - Error code
 * @returns {boolean} True if error is retryable
 */
export function isRetryable(code) {
  const error = getErrorByCode(code);
  return error ? error.retryable : false;
}

/**
 * Get retry configuration for error
 * @param {string} code - Error code
 * @returns {Object} Retry configuration {maxRetries, retryDelay}
 */
export function getRetryConfig(code) {
  const error = getErrorByCode(code);
  if (!error || !error.retryable) {
    return { maxRetries: 0, retryDelay: 0 };
  }
  
  return {
    maxRetries: error.maxRetries || 1,
    retryDelay: error.retryDelay || 1000
  };
}

/**
 * Get error category
 * @param {string} code - Error code
 * @returns {string} Error category
 */
export function getErrorCategory(code) {
  const error = getErrorByCode(code);
  return error ? error.category : ErrorCategory.UNKNOWN;
}

/**
 * Get error severity
 * @param {string} code - Error code
 * @returns {string} Error severity
 */
export function getErrorSeverity(code) {
  const error = getErrorByCode(code);
  return error ? error.severity : ErrorSeverity.ERROR;
}

/**
 * Check if error is critical
 * @param {string} code - Error code
 * @returns {boolean} True if error is critical
 */
export function isCriticalError(code) {
  return getErrorSeverity(code) === ErrorSeverity.CRITICAL;
}

/**
 * Parse error from CDP response
 * @param {Object} response - CDP response object
 * @returns {Object} Parsed error {code, message, userMessage, ...}
 */
export function parseError(response) {
  if (!response || !response.error) {
    return {
      code: '9999',
      message: 'Error desconocido',
      userMessage: 'Ocurrió un error inesperado',
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.ERROR,
      retryable: false
    };
  }

  // Try to extract error code from response
  const errorCode = response.errorCode || response.code || '9999';
  const errorDetails = getErrorByCode(errorCode);

  if (errorDetails) {
    return {
      ...errorDetails,
      originalResponse: response
    };
  }

  // Unknown error code - return generic error
  return {
    code: errorCode,
    message: response.error || 'Error desconocido',
    userMessage: 'Ocurrió un error inesperado',
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.ERROR,
    retryable: false,
    originalResponse: response
  };
}

/**
 * Create error object
 * @param {string} code - Error code
 * @param {Object} additionalData - Additional error data
 * @returns {Error} Error object with extended properties
 */
export function createError(code, additionalData = {}) {
  const errorDetails = getErrorByCode(code);
  
  if (!errorDetails) {
    const error = new Error(`Unknown error code: ${code}`);
    error.code = code;
    error.category = ErrorCategory.UNKNOWN;
    error.severity = ErrorSeverity.ERROR;
    return error;
  }

  const error = new Error(errorDetails.message);
  error.code = errorDetails.code;
  error.userMessage = errorDetails.userMessage;
  error.category = errorDetails.category;
  error.severity = errorDetails.severity;
  error.retryable = errorDetails.retryable;
  
  // Add additional data
  Object.assign(error, additionalData);
  
  return error;
}

const PINPAD_V2_SYMBOLIC_ERROR_CODES = {
  USER_CANCELLED: {
    code: 'USER_CANCELLED',
    userMessage: 'Has cancelado la operación, puedes intentarlo de nuevo.',
    severity: ErrorSeverity.INFO,
    retryable: true,
  },
  READ_CARD_TIMEOUT: {
    code: 'READ_CARD_TIMEOUT',
    userMessage: 'No fue posible leer tu tarjeta en el tiempo establecido, inténtalo nuevamente.',
    severity: ErrorSeverity.WARNING,
    retryable: true,
  },
  READ_CARD_FAILED: {
    code: 'READ_CARD_FAILED',
    userMessage: 'No fue posible leer tu tarjeta, inténtalo nuevamente.',
    severity: ErrorSeverity.WARNING,
    retryable: true,
  },
  TRANSACTION_DENIED: {
    code: 'TRANSACTION_DENIED',
    userMessage: 'La transacción fue rechazada. Verifica con tu banco o intenta con otra tarjeta.',
    severity: ErrorSeverity.ERROR,
    retryable: true,
  },
  TRANSACTION_NOT_APPROVED: {
    code: 'TRANSACTION_NOT_APPROVED',
    userMessage: 'La transacción no fue aprobada.',
    severity: ErrorSeverity.ERROR,
    retryable: true,
  },
  MERCHANT_NOT_FOUND: {
    code: 'MERCHANT_NOT_FOUND',
    userMessage: 'No se encontró información del comercio para tu tarjeta.',
    severity: ErrorSeverity.ERROR,
    retryable: false,
  },
  TIMEOUT: {
    code: 'TIMEOUT',
    userMessage: 'La operación excedió el tiempo de espera. Por favor, intenta nuevamente.',
    severity: ErrorSeverity.WARNING,
    retryable: true,
  },
  LOGIN_REQUIRED: {
    code: 'LOGIN_REQUIRED',
    userMessage: 'El terminal PinPad requiere reconfiguración. Contacta al administrador.',
    severity: ErrorSeverity.CRITICAL,
    retryable: false,
  },
  NOT_INITIALIZED: {
    code: 'NOT_INITIALIZED',
    userMessage: 'El terminal PinPad no está configurado. Contacta al administrador.',
    severity: ErrorSeverity.CRITICAL,
    retryable: false,
  },
  VOUCHER_PRINT_FAILED: {
    code: 'VOUCHER_PRINT_FAILED',
    userMessage: 'La transacción fue aprobada, pero no se pudo imprimir el voucher.',
    severity: ErrorSeverity.WARNING,
    retryable: false,
  },
};

export function getPinpadErrorMessage(code, fallbackMessage = '') {
  const normalizedCode = String(code || '9999').trim().toUpperCase();

  if (PINPAD_V2_SYMBOLIC_ERROR_CODES[normalizedCode]) {
    return PINPAD_V2_SYMBOLIC_ERROR_CODES[normalizedCode];
  }

  const numericError = getErrorByCode(normalizedCode);
  if (numericError) {
    return {
      code: numericError.code,
      userMessage: numericError.userMessage,
      severity: numericError.severity,
      retryable: numericError.retryable,
    };
  }

  return {
    code: normalizedCode,
    userMessage: fallbackMessage || 'Ocurrió un error inesperado. Por favor, intenta nuevamente.',
    severity: ErrorSeverity.ERROR,
    retryable: true,
  };
}

export default CDP_ERROR_CODES;
